import { useLayoutEffect, useRef, useState, type ReactNode, type SyntheticEvent } from 'react';
import { CloseRounded } from '@mui/icons-material';
import { Alert, Box, IconButton, Portal, Snackbar, Stack, type AlertColor, type SnackbarCloseReason } from '@mui/material';
import { designTokens } from '../tokens';
import { createFocusRingStyles } from '../interaction';

export type FeedbackSnackbarProps = {
  open: boolean;
  message: ReactNode;
  onClose: () => void;
  tone?: AlertColor;
  autoHideDuration?: number | null;
  closeLabel?: string;
  action?: ReactNode;
  notificationKey?: number | string;
};

/**
 * 화면 전환과 무관한 짧은 처리 결과를 알립니다. 위치, live region과 닫기 동작은 공용 계약이 소유합니다.
 */
export function FeedbackSnackbar({
  open,
  message,
  onClose,
  tone = 'info',
  autoHideDuration,
  closeLabel = '알림 닫기',
  action,
  notificationKey,
}: FeedbackSnackbarProps) {
  const [host] = useState(() => typeof document === 'undefined' ? null : document.createElement('div'));
  const returnFocus = useRef<HTMLElement | null>(null);
  const restoreFocus = () => {
    const target = returnFocus.current;
    if (host?.contains(host.ownerDocument.activeElement) && target?.isConnected && !target.closest('[aria-hidden="true"], [inert]')) target.focus({ preventScroll: true });
  };
  const close = () => { restoreFocus(); onClose(); };
  useLayoutEffect(() => {
    if (!host || !open) return;
    const ownerDocument = host.ownerDocument;
    const rememberFocus = (event: FocusEvent) => {
      if (event.relatedTarget instanceof HTMLElement && !host.contains(event.relatedTarget)) returnFocus.current = event.relatedTarget;
    };
    host.addEventListener('focusin', rememberFocus);
    const placeHost = () => {
      const modal = Array.from(ownerDocument.querySelectorAll<HTMLElement>('.MuiModal-root')).reverse().find((element) =>
        !element.closest('[aria-hidden="true"], [inert]') && !element.classList.contains('MuiModal-hidden') && element.getClientRects().length > 0,
      );
      // Dialog·Drawer·Menu의 focus trap 안에 두어 읽기와 버튼 조작을 함께 유지합니다.
      const container = modal?.querySelector<HTMLElement>('[role="dialog"], .MuiDialog-container, .MuiDrawer-paper, .MuiPopover-paper') ?? modal ?? ownerDocument.body;
      if (host.parentElement !== container) container.appendChild(host);
      if (host.hasAttribute('aria-hidden')) host.removeAttribute('aria-hidden');
    };
    placeHost();
    const observer = new MutationObserver(placeHost);
    observer.observe(ownerDocument.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-hidden', 'inert', 'class'] });
    return () => {
      observer.disconnect();
      host.removeEventListener('focusin', rememberFocus);
      const target = returnFocus.current;
      if (host.contains(ownerDocument.activeElement) && target?.isConnected && !target.closest('[aria-hidden="true"], [inert]')) target.focus({ preventScroll: true });
      host.remove();
    };
  }, [host, open]);
  const urgent = tone === 'error' || tone === 'warning';
  const duration = autoHideDuration === undefined ? (urgent || action ? 6_000 : 3_000) : autoHideDuration;
  const handleClose = (_event: Event | SyntheticEvent, reason?: SnackbarCloseReason) => {
    if (reason !== 'clickaway') close();
  };

  if (!host) return null;
  return <Portal container={host}>
    <Snackbar
      key={notificationKey}
      open={open}
      autoHideDuration={duration}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{ width: designTokens.feedback.toast.width, maxWidth: 'calc(100vw - 32px)', mx: 'auto' }}
    >
      <Alert
        role={urgent ? 'alert' : 'status'}
        aria-live={urgent ? 'assertive' : 'polite'}
        aria-atomic="true"
        severity={tone}
        variant="outlined"
        closeText={closeLabel}
        action={<IconButton aria-label={closeLabel} title={closeLabel} size="small" color="inherit" onClick={close}><CloseRounded fontSize="small" /></IconButton>}
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          minWidth: 0,
          padding: `${designTokens.feedback.toast.blockInset}px ${designTokens.feedback.toast.inlineInset}px`,
          borderRadius: designTokens.feedback.toast.radius,
          borderColor: designTokens.color.divider,
          bgcolor: designTokens.color.canvas,
          color: designTokens.color.primaryText,
          boxShadow: designTokens.feedback.toast.shadow,
          '& .MuiAlert-message': { flex: 1 },
          '& .MuiAlert-icon': { flexShrink: 0, color: tone === 'error' ? designTokens.color.dangerText : tone === 'warning' ? designTokens.color.warningDark : tone === 'success' ? designTokens.color.successText : designTokens.color.infoDark },
          '& .MuiAlert-action': { mt: 0, flexShrink: 0, alignSelf: 'flex-start' },
          '& .MuiButtonBase-root.Mui-focusVisible': createFocusRingStyles({ inset: true }),
        }}
      >
        <Stack spacing={1} sx={{ minWidth: 0, alignItems: 'flex-start' }}>
          <Box>{message}</Box>
          {action}
        </Stack>
      </Alert>
    </Snackbar>
  </Portal>;
}
